import React from "react";
import { useLocation } from "react-router-dom";
import ContactInformation from "components/card/contact-information/ContactInformation";
import StatementOfHealth from "components/card/statement-of-health/StatementOfHealth";
import CoverageReplacement from "components/card/coverage-replacement/CoverageReplacement";
import PolicyBeneficiary from "components/card/policy-beneficiary/PolicyBeneficiary";
import SecondaryAddressee from "components/card/secondary-addressee/SecondaryAddressee";
import CardNavigationButtons from "components/card-navigation-button/CardNavigationButtons";
import CardSkeleton from "components/card/card-skeleton/CardSkeleton";
import CardTemplate from "components/card/card-template/CardTemplate";
import ErrorBoundary from "components/error-boundary/ErrorBoundary";
import { APPLICATION_CARD_ORDER } from "constants/pageNames";
import { useContactInformationPage } from "hooks/contact-information/useContactInformationPage";

const contactInformationTagId = "contact-information";
const statementOfHealthTagId = "statement-of-health";
const coverageReplacementTagId = "coverage-replacement";
const policyBeneficiaryTagId = "policy-beneficiary";
const secondaryAddresseeTagId = "secondary-addressee";

/** Fires the card auto-save only when focus leaves the card container entirely. */
const handleCardSectionBlur = (e: React.FocusEvent, cardId: string, onBlur: (cardId: string) => void) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
        onBlur(cardId);
    }
};

/** Error fallback renderer shared by every card's ErrorBoundary. */
const renderCardErrorFallback = (cardLabel: string) => (_error: Error, reset: () => void) =>
    (
        <CardTemplate title={cardLabel}>
            <div data-testid={`card-error-fallback-${cardLabel}`} className="flex flex-col gap-3 pt-4 text-base-content">
                <p className="text-sm leading-5">
                    We couldn't load this section. You can try again or continue with the rest of your application.
                </p>
                <button type="button" className="btn btn-primary self-start h-[36px]" onClick={reset}>
                    Try Again
                </button>
            </div>
        </CardTemplate>
    );

const ContactInformationPage = () => {
    // ========== Hooks ==========
    const location = useLocation();
    const {
        form,
        isDTO,
        isGIWL,
        isMissingRequiredData,
        isLoadingData,
        optimisticSaveStatus,
        isNextButtonDisabled,
        isPending,
        contactInformationRef,
        statementOfHealthRef,
        coverageReplacementRef,
        policyBeneficiaryRef,
        secondaryAddresseeRef,
        handleCardBlur,
        handleBack,
        formAction,
    } = useContactInformationPage();

    // ========== Render ==========
    if (isMissingRequiredData) return null;

    // Show skeleton loaders while loading saved application data
    if (isLoadingData) {
        const skeletonCount = isDTO ? 5 : 3;
        return (
            <>
                {Array.from({ length: skeletonCount }).map((_, index) => (
                    <CardSkeleton key={index} />
                ))}
                <CardNavigationButtons
                    onBack={handleBack}
                    onNext={formAction}
                    isLoading={true}
                    disabled={true}
                    hideBack={isGIWL}
                />
            </>
        );
    }

    const coverageReplacementDiv = (
        <ErrorBoundary fallback={renderCardErrorFallback("Coverage Replacement")} resetKey={location.pathname}>
            <div
                id={coverageReplacementTagId}
                onBlur={(e) => handleCardSectionBlur(e, APPLICATION_CARD_ORDER[2], handleCardBlur)}
            >
                <CoverageReplacement ref={coverageReplacementRef} form={form} />
            </div>
        </ErrorBoundary>
    );

    return (
        <>
            <title>Contact Info | AAA Life</title>
            <ErrorBoundary fallback={renderCardErrorFallback("Contact Information")} resetKey={location.pathname}>
                <div
                    id={contactInformationTagId}
                    onBlur={(e) => handleCardSectionBlur(e, APPLICATION_CARD_ORDER[0], handleCardBlur)}
                >
                    <ContactInformation ref={contactInformationRef} form={form} />
                </div>
            </ErrorBoundary>
            {isDTO && (
                <ErrorBoundary fallback={renderCardErrorFallback("Statement of Health")} resetKey={location.pathname}>
                    <div
                        id={statementOfHealthTagId}
                        onBlur={(e) => handleCardSectionBlur(e, APPLICATION_CARD_ORDER[1], handleCardBlur)}
                    >
                        <StatementOfHealth ref={statementOfHealthRef} form={form} />
                    </div>
                </ErrorBoundary>
            )}
            {isDTO && coverageReplacementDiv}
            <ErrorBoundary fallback={renderCardErrorFallback("Policy Beneficiary")} resetKey={location.pathname}>
                <div
                    id={policyBeneficiaryTagId}
                    onBlur={(e) => handleCardSectionBlur(e, APPLICATION_CARD_ORDER[3], handleCardBlur)}
                >
                    <PolicyBeneficiary ref={policyBeneficiaryRef} form={form} />
                </div>
            </ErrorBoundary>
            <ErrorBoundary fallback={renderCardErrorFallback("Secondary Addressee")} resetKey={location.pathname}>
                <div
                    id={secondaryAddresseeTagId}
                    onBlur={(e) => handleCardSectionBlur(e, APPLICATION_CARD_ORDER[4], handleCardBlur)}
                >
                    <SecondaryAddressee ref={secondaryAddresseeRef} form={form} />
                </div>
            </ErrorBoundary>
            {isGIWL && coverageReplacementDiv}
            <div className="flex justify-end min-h-[20px]">
                {optimisticSaveStatus === "saving" && <span className="text-xs text-base-content/60">Saving...</span>}
                {optimisticSaveStatus === "saved" && <span className="text-xs text-success">✓ Saved</span>}
                {optimisticSaveStatus === "error" && <span className="text-xs text-error">Failed to save</span>}
            </div>
            <CardNavigationButtons
                onBack={handleBack}
                onNext={formAction}
                isLoading={isPending}
                disabled={isNextButtonDisabled}
                hideBack={isGIWL}
            />
        </>
    );
};

export { ContactInformationPage };
export default ContactInformationPage;
